// ⬡B:msria-home.schedule:SURFACE:schedule_booking_glass:20260608⬡
// Schedule surface embedded in the React app — no separate static HTML needed.
// /schedule       → sign-in → resolves HAM → redirects to /schedule/{uid}
// /schedule/:uid  → public booking surface (no sign-in needed for bookers)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHam, signInFailureMessage } from '@aba/ham-core';
import { Smoke, C } from '@aba/ms-mac-core';

// ⬡B:aba_shared.msria_home.schedule:FIX:live_door_env_first_default:20260801⬡
// window.__ENV__.REACH_URL was never set anywhere in this app (no injection script, no
// runtime config file, checked in source and the built dist bundle), so this constant
// always fell through to aba-reach.onrender.com -- suspended since the reach-services
// retirement (anew routes/reach.routes.js: "Canonical REACH now runs only from durable
// PAI cycle candidates," 20260717) -- and every real visitor's booking attempt silently
// failed. The real booking backend (core/schedule/schedule.logic.js, the same
// /api/schedule/:uid/availability and /api/schedule/:uid/book contract) now lives on the
// mind, moved home 20260709 (anew routes/schedule.routes.js). Switched to the Vite
// build-time env pattern already used one file over in this same app (App.jsx) and in
// packages/api-core/src/client.js and awa-core's MockInterviewVARA. Verified live
// 20260801: GET aibebase.onrender.com/api/schedule/:uid/availability answers a real
// application JSON body, not a suspended-host page.
const REACH = (import.meta.env && import.meta.env.VITE_REACH_API_BASE) || 'https://aibebase.onrender.com';
const AIR   = (import.meta.env && import.meta.env.VITE_ABA_API_BASE)   || 'https://ababase.onrender.com';

// ── HAM owner sign-in at /schedule (no UID) ──────────────────────────────────
function ScheduleSignIn() {
  const { ham, loading, signInWithGoogle } = useHam();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  useEffect(() => {
    if (loading || !ham) return;
    // Resolve the HAM's UID and redirect to their schedule page
    nav(`/schedule/${ham.acl_uid || ham.hamUid}`, { replace: true });
  }, [ham, loading, nav]);

  // ⬡B:DC499D0C:FIX:sign_in_never_a_dead_button:20260801⬡
  // TM-9 neighbour sweep. This one always said something, so it was never dead. It
  // said the same six words for every cause, including the one nobody standing here
  // can fix. Reading from the shared source so it names what actually happened.
  async function google() {
    setBusy(true);
    setErr('');
    try {
      if (typeof signInWithGoogle !== 'function') throw new Error('sign in unavailable');
      await signInWithGoogle();
    }
    catch (e) { setErr(signInFailureMessage(e)); setBusy(false); }
  }

  return (
    <div style={{ position:'relative', minHeight:'100dvh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px 20px', background: C.ink, color: C.text, fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Inter,system-ui,sans-serif' }}>
      <Smoke />
      <div style={{ position:'relative', zIndex:2, textAlign:'center', maxWidth:380 }}>
        <h1 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:8 }}>Ms.Ria Schedule</h1>
        <p style={{ color:C.muted, fontSize:15, marginBottom:32, lineHeight:1.6 }}>Sign in to access your scheduling page.</p>
        <button
          onClick={google}
          disabled={busy || loading}
          style={{ display:'block', width:'100%', padding:'14px 24px', borderRadius:14, background:'linear-gradient(135deg,#e8b4a0,#b08d57)', color:'#0a0a0c', fontWeight:700, fontSize:16, border:'none', cursor:'pointer', marginBottom:12 }}>
          {busy ? 'Redirecting…' : 'Continue with Google'}
        </button>
        {err && <p style={{ color:'#e8a0a0', fontSize:13, marginTop:8 }}>{err}</p>}
      </div>
    </div>
  );
}

// ── Public booking surface at /schedule/:uid ──────────────────────────────────
function ScheduleBooking() {
  const { uid } = useParams();
  const [slots,    setSlots]    = useState([]);
  const [settings, setSettings] = useState({});
  const [tokens,   setTokens]   = useState(null);
  const [loadErr,  setLoadErr]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [step,     setStep]     = useState('slots'); // slots | form | done | pending
  const [selected, setSelected] = useState(null);
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [note,     setNote]     = useState('');
  const [booking,  setBooking]  = useState(false);
  const [bookErr,  setBookErr]  = useState('');

  useEffect(() => {
    if (!uid) return;
    fetch(`${REACH}/api/schedule/${uid}/availability`, { headers:{'Content-Type':'application/json'} })
      .then(r => r.json())
      .then(d => {
        if (!d.ok && !d.slots) throw new Error(d.error || 'Failed to load');
        setSlots(d.slots || []);
        setSettings(d.settings || {});
        setTokens(d.tokens || null);
        setLoading(false);
      })
      .catch(e => {
        // ⬡B:aba_shared.msria_home.schedule:FIX:human_words_not_backend_leakage:20260801⬡
        // This used to render e.message straight to the visitor -- a JSON parse error, a
        // network failure, or a raw backend string, whichever fired. Front end words serve
        // the human reading them: one calm sentence, no host, no status code, no backend
        // detail. The real reason still goes to the console for whoever is looking.
        console.error('[schedule] availability load failed:', e && e.message);
        setLoadErr("Times aren't available to book right now. Please check back shortly.");
        setLoading(false);
      });
  }, [uid]);

  const bg = tokens && tokens.backgrounds && settings.background
    ? tokens.backgrounds[settings.background]
    : null;
  const bgCss = bg ? (bg.imgUrl ? `url(${bg.imgUrl}) center/cover no-repeat` : bg.css)
    : 'linear-gradient(135deg, #0d0d14 0%, #130d1a 50%, #0d0d14 100%)';

  const fmt = ts => {
    const d = new Date(ts * 1000);
    return d.toLocaleString('en-US', { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit', hour12:true, timeZone:'America/New_York' });
  };

  const book = useCallback(async () => {
    if (!selected || !name.trim() || !email.trim()) return;
    setBooking(true);
    setBookErr('');
    try {
      const r = await fetch(`${REACH}/api/schedule/${uid}/book`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ slotStart: selected.start, slotEnd: selected.end, name: name.trim(), email: email.trim(), note: note.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Booking failed');
      setStep(d.confirmed ? 'done' : 'pending');
    } catch(e) {
      // ⬡B:aba_shared.msria_home.schedule:FIX:no_native_dialog_on_booking_page:20260801⬡
      // Was alert(e.message): an OS-chrome grey box carrying a raw backend string, the exact
      // shape the sibling booking surface (anew routes/face/schedule.page.js) already
      // documented moving away from ("no native dialogs on the booking page"). Inline,
      // human, on the page it already is.
      console.error('[schedule] booking failed:', e && e.message);
      setBookErr("Your booking didn't go through. Please try again.");
    }
    setBooking(false);
  }, [selected, name, email, note, uid]);

  const glass = { background:'rgba(255,255,255,0.06)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:16 };
  const inp   = { width:'100%', boxSizing:'border-box', padding:'12px 14px', borderRadius:10, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', color:'#f0ead6', fontSize:14, outline:'none', fontFamily:'inherit', marginBottom:10 };
  const btn   = { display:'block', width:'100%', padding:'13px', borderRadius:12, background:'linear-gradient(135deg,#e8b4a0,#b08d57)', color:'#0a0a0c', fontWeight:700, fontSize:15, border:'none', cursor:'pointer', marginTop:4 };

  return (
    <div style={{ position:'relative', minHeight:'100dvh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'32px 20px', fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Inter,system-ui,sans-serif' }}>
      <div style={{ position:'fixed', inset:0, background:bgCss, zIndex:0 }} />
      <div style={{ position:'relative', zIndex:1, width:'100%', maxWidth:420 }}>
        {loading && <p style={{ color:'rgba(240,234,214,0.6)', textAlign:'center' }}>Loading availability…</p>}
        {loadErr && <p style={{ color:'#e8a0a0', textAlign:'center' }}>{loadErr}</p>}

        {!loading && !loadErr && step === 'slots' && (
          <div style={{ ...glass, padding:'28px 24px' }}>
            <h2 style={{ color:'#f0ead6', fontSize:22, fontWeight:700, marginBottom:4 }}>Book a 1:1</h2>
            <p style={{ color:'rgba(240,234,214,0.5)', fontSize:13, marginBottom:20 }}>Select a time that works for you.</p>
            <div style={{ maxHeight:320, overflowY:'auto' }}>
              {slots.length === 0 && <p style={{ color:'rgba(240,234,214,0.4)', fontSize:14 }}>No slots available right now. Check back soon.</p>}
              {slots.map((s,i) => (
                <button key={i} onClick={() => { setSelected(s); setStep('form'); }}
                  style={{ display:'block', width:'100%', textAlign:'left', padding:'12px 14px', borderRadius:10, background: 'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', color:'#f0ead6', fontSize:14, cursor:'pointer', marginBottom:8, fontFamily:'inherit' }}>
                  {fmt(s.start)}
                </button>
              ))}
            </div>
          </div>
        )}

        {!loading && step === 'form' && (
          <div style={{ ...glass, padding:'28px 24px' }}>
            <button onClick={() => setStep('slots')} style={{ background:'none', border:'none', color:'rgba(240,234,214,0.5)', cursor:'pointer', padding:0, marginBottom:16, fontSize:13 }}>← Back</button>
            <h2 style={{ color:'#f0ead6', fontSize:20, fontWeight:700, marginBottom:4 }}>Your info</h2>
            <p style={{ color:'rgba(240,234,214,0.5)', fontSize:13, marginBottom:18 }}>{fmt(selected.start)}</p>
            <input style={inp} placeholder="Your name" value={name} onChange={e=>setName(e.target.value)} />
            <input style={inp} type="email" placeholder="Your email" value={email} onChange={e=>setEmail(e.target.value)} />
            <textarea style={{...inp, resize:'vertical', minHeight:70}} placeholder="Anything to share? (optional)" value={note} onChange={e=>setNote(e.target.value)} />
            <button style={btn} onClick={book} disabled={booking || !name.trim() || !email.trim()}>
              {booking ? 'Booking…' : 'Confirm Booking'}
            </button>
            {bookErr && <p style={{ color:'#e8a0a0', fontSize:13, marginTop:10, textAlign:'center' }}>{bookErr}</p>}
          </div>
        )}

        {step === 'done' && (
          <div style={{ ...glass, padding:'32px 24px', textAlign:'center' }}>
            <p style={{ fontSize:28, marginBottom:8 }}>✓</p>
            <h2 style={{ color:'#f0ead6', fontSize:20, fontWeight:700, marginBottom:8 }}>You're booked</h2>
            <p style={{ color:'rgba(240,234,214,0.6)', fontSize:14 }}>A calendar invite is on its way.</p>
          </div>
        )}

        {step === 'pending' && (
          <div style={{ ...glass, padding:'32px 24px', textAlign:'center' }}>
            <p style={{ fontSize:28, marginBottom:8 }}>⏳</p>
            <h2 style={{ color:'#f0ead6', fontSize:20, fontWeight:700, marginBottom:8 }}>Request sent</h2>
            <p style={{ color:'rgba(240,234,214,0.6)', fontSize:14 }}>We'll confirm your time shortly.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Schedule() {
  const { uid } = useParams();
  return uid ? <ScheduleBooking /> : <ScheduleSignIn />;
}
